import { Col, ExtraInfo, Loading, Row, Table } from '@elotech/components';
import { AxiosPromise } from 'axios';
import {
  IFrame,
  IFrameContainer,
  InlineButton,
  TableContainer
} from 'common/components';
import { Arquivo } from 'common/type';
import { formatarData, getContentType, getExtension } from 'common/utils';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Image } from 'react-bootstrap';

import styles from './documentViewer.module.css';

type Props = {
  documents: Arquivo[];
  onDownload: (arquivo: Arquivo) => AxiosPromise;
  parentIsLoading: boolean;
  getDocumentCaption?: (document: Arquivo) => React.ReactNode;
  canDownload?: boolean;
  introRef?: React.Ref<HTMLDivElement>;
};

const VIEW_TYPES = [
  'PDF',
  'PNG',
  'JPG',
  'JPEG',
  'GIF',
  'BMP',
  'SVG',
  'MP4',
  'WEBM',
  'OGV',
  'OGG'
];

const IMAGE_TYPES = ['PNG', 'JPG', 'JPEG', 'GIF', 'BMP', 'SVG'];

const ESPACO_SEM_QUEBRA = String.fromCharCode(160);

let contadorSelect = 0;

const mesmoDocumento = (primeiro?: Arquivo, segundo?: Arquivo) =>
  !!primeiro &&
  !!segundo &&
  (primeiro === segundo ||
    (!!primeiro.id && primeiro.id === segundo.id) ||
    (!!primeiro.fullPath && primeiro.fullPath === segundo.fullPath));

const nomeDocumento = (doc?: Arquivo) => doc?.nome || 'Documento sem nome';

const quantidadeDocumentos = (total: number) =>
  `${total} ${total === 1 ? 'documento' : 'documentos'}`;

const textoIntro = (total: number) =>
  total
    ? `Visualizador de documentos. Visualizando ${quantidadeDocumentos(
        total
      )}. Escolha um documento na lista e pressione Abrir documento. Pressione ESC para fechar. Dentro do documento, use Shift+Tab para voltar aos controles.`
    : 'Visualizador de documentos. Nenhum documento para visualizar. Pressione ESC para fechar.';

const formatarDataCriacao = (data?: string) => {
  if (!data) {
    return undefined;
  }

  try {
    return formatarData(data);
  } catch (error) {
    return undefined;
  }
};

const descreverPosicao = (index: number, total: number) => {
  if (total === 1) {
    return 'Único documento';
  }

  if (index === 0) {
    return `Primeiro documento, 1 de ${total}`;
  }

  if (index === total - 1) {
    return `Último documento, ${total} de ${total}`;
  }

  return `Documento ${index + 1} de ${total}`;
};

export const DocumentViewer: React.FC<Props> = ({
  documents,
  onDownload,
  parentIsLoading,
  getDocumentCaption,
  canDownload = true,
  introRef
}) => {
  const [showingFile, setShowingFile] = useState<Arquivo>();
  const [showingLink, setShowingLink] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [anuncio, setAnuncio] = useState('');
  const [focarTitulo, setFocarTitulo] = useState(false);
  const tituloRef = useRef<HTMLHeadingElement>(null);
  const estavaCarregando = useRef(parentIsLoading);
  const selectId = useRef(`document-viewer-select-${++contadorSelect}`);

  const indiceDe = (doc?: Arquivo) =>
    documents.findIndex(documento => mesmoDocumento(documento, doc));

  const descreverDocumento = (doc: Arquivo) => {
    const caption = getDocumentCaption?.(doc);
    const dataCriacao = formatarDataCriacao(doc.dataCriacao);

    return [
      nomeDocumento(doc),
      typeof caption === 'string' && caption,
      !VIEW_TYPES.includes(getExtension(doc.nome)) &&
        'disponível apenas para download',
      doc.possuiAssinaturaDigital && 'assinado digitalmente',
      dataCriacao && `enviado em ${dataCriacao}`,
      doc.usuarioResponsavel && `enviado por ${doc.usuarioResponsavel}`
    ]
      .filter(Boolean)
      .join(', ');
  };

  const descreverTitulo = (doc?: Arquivo) => {
    const index = indiceDe(doc);

    if (!doc || index < 0) {
      return 'Nenhum documento aberto. Escolha um documento na lista e pressione Abrir documento.';
    }

    return `${descreverPosicao(index, documents.length)}: ${descreverDocumento(
      doc
    )}`;
  };

  const anunciar = (mensagem: string) =>
    setAnuncio(anterior =>
      anterior === mensagem ? `${mensagem}${ESPACO_SEM_QUEBRA}` : mensagem
    );

  const doDownloadFile = useCallback(() => {
    const link = document.createElement('a');
    link.href = showingLink!;
    link.download = showingFile!.nome!;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [showingLink, showingFile]);

  useEffect(() => {
    if (!showingFile) {
      return;
    }
    let fileURL: string;
    let canceled = false;

    setShowingLink(undefined);
    setLoading(true);
    onDownload(showingFile!)
      .then(({ data, headers }) => {
        if (canceled) {
          return;
        }

        const contentDisposition = headers?.['Content-Disposition'];
        const responseFileName = contentDisposition?.substring(
          contentDisposition.indexOf('"') + 1,
          contentDisposition.lastIndexOf('"')
        );

        const type = getContentType(responseFileName || showingFile!.nome);
        const blob = new Blob([data], { type });

        fileURL = URL.createObjectURL(blob);

        setShowingLink(fileURL);
      })
      .finally(() => {
        if (!canceled) {
          setLoading(false);
        }
      });

    return () => {
      canceled = true;
      URL.revokeObjectURL(fileURL);
    };
  }, [showingFile]);

  useEffect(() => {
    const index = indiceDe(showingFile);

    if (index >= 0) {
      setSelectedIndex(index);
    }
  }, [showingFile]);

  useEffect(() => {
    if (!focarTitulo) {
      return;
    }

    tituloRef.current?.focus();
    setFocarTitulo(false);
  }, [focarTitulo]);

  useEffect(() => {
    if (estavaCarregando.current && !parentIsLoading) {
      anunciar(textoIntro(documents.length));
    }

    estavaCarregando.current = parentIsLoading;
  }, [parentIsLoading]);

  const getNext = (forward: boolean) => {
    const canViewList = documents.filter(doc =>
      VIEW_TYPES.includes(getExtension(doc.nome))
    );

    if (!canViewList.length) {
      return undefined;
    }

    if (
      canViewList.length === 1 &&
      mesmoDocumento(showingFile, canViewList[0])
    ) {
      return canViewList[0];
    }

    if (!showingFile) {
      return canViewList[forward ? 0 : canViewList.length - 1];
    }

    let nextIndex =
      canViewList.findIndex(doc => mesmoDocumento(doc, showingFile)) +
      (forward ? 1 : -1);

    if (nextIndex >= canViewList.length) {
      nextIndex = 0;
    }

    if (nextIndex < 0) {
      nextIndex = canViewList.length - 1;
    }

    return canViewList[nextIndex];
  };

  const onNext = (forward: boolean) => {
    const next = getNext(forward);

    if (!next) {
      anunciar('Nenhum documento disponível para visualização.');
      return;
    }

    setShowingFile(next);
    anunciar(descreverTitulo(next));
  };

  const abrirSelecionado = () => {
    const doc = documents[selectedIndex];

    if (!doc) {
      return;
    }

    setShowingFile(doc);
    setFocarTitulo(true);
  };

  const isImage = () => {
    return showingLink && IMAGE_TYPES.includes(getExtension(showingFile?.nome));
  };

  const getViewerPath = () => {
    if (!showingLink) {
      return 'about:blank';
    }

    if (!canDownload && getExtension(showingFile?.nome) === 'PDF') {
      return `${showingLink}#toolbar=0`;
    }

    return showingLink;
  };

  const intro = parentIsLoading
    ? 'Visualizador de documentos. Carregando documentos.'
    : textoIntro(documents.length);

  return (
    <>
      <Row>
        <Col sm={3} md={3}>
          <div className="sr-only" tabIndex={-1} ref={introRef}>
            {intro}
          </div>
          <div className="sr-only" role="status" aria-live="polite">
            {anuncio}
          </div>
          {!parentIsLoading && documents.length > 0 && (
            <div className="sr-only">
              <label htmlFor={selectId.current}>Documento</label>
              <select
                id={selectId.current}
                value={selectedIndex}
                onChange={event => setSelectedIndex(Number(event.target.value))}
              >
                {documents.map((doc, index) => (
                  <option key={`${index} - ${doc.nome}`} value={index}>
                    {`${index + 1} de ${documents.length}: ${descreverDocumento(
                      doc
                    )}`}
                  </option>
                ))}
              </select>
              <button type="button" onClick={abrirSelecionado}>
                Abrir documento
              </button>
            </div>
          )}
          <div aria-hidden="true">
            <TableContainer>
              <Table<Arquivo>
                values={documents}
                highlight={doc => doc === showingFile}
                onClickRow={setShowingFile}
                keyExtractor={(doc, index) => `${index} - ${doc.nome}`}
                loading={parentIsLoading}
              >
                <Table.Column<Arquivo>
                  style={{ cursor: 'pointer' }}
                  header="Nome Documento"
                  value={doc => {
                    const caption = getDocumentCaption?.(doc);

                    return (
                      <div className={styles.documentName}>
                        <div className={styles.documentTitle}>
                          {!VIEW_TYPES.includes(getExtension(doc?.nome)) && (
                            <ExtraInfo
                              letter={'D'}
                              color={'negative'}
                              value={'Apenas Download'}
                            />
                          )}
                          <span>{doc.nome}</span>
                          {!!doc?.possuiAssinaturaDigital && (
                            <i
                              aria-label="Documento com assinatura digital"
                              className={`fa fa-signature ${styles.signatureIndicator}`}
                              role="img"
                              title="Documento com assinatura digital"
                            />
                          )}
                        </div>
                        {caption && (
                          <small className={styles.documentCaption}>
                            {caption}
                          </small>
                        )}
                      </div>
                    );
                  }}
                />
              </Table>
            </TableContainer>
          </div>
        </Col>
        <Col sm={9} md={9}>
          <Row>
            <div className="container" style={{ marginTop: '20px' }}>
              <Col sm={canDownload ? 5 : 6} md={canDownload ? 5 : 6}>
                <InlineButton
                  style={{ width: '100%' }}
                  label="Anterior"
                  aria-label="Documento anterior"
                  icon="fa fa-arrow-left"
                  onClick={() => {
                    onNext(false);
                  }}
                  type="button"
                />
              </Col>
              {canDownload && (
                <Col sm={2} md={2}>
                  <InlineButton
                    style={{ width: '100%' }}
                    label="Baixar"
                    aria-label={
                      showingFile
                        ? `Baixar ${nomeDocumento(showingFile)}`
                        : undefined
                    }
                    icon="fa fa-download"
                    disabled={!showingFile || !showingLink}
                    onClick={doDownloadFile}
                    type="button"
                  />
                </Col>
              )}
              <Col sm={canDownload ? 5 : 6} md={canDownload ? 5 : 6}>
                <InlineButton
                  style={{ width: '100%' }}
                  label="Próximo"
                  aria-label="Próximo documento"
                  icon="fa fa-arrow-right"
                  className="icon-right"
                  onClick={() => {
                    onNext(true);
                  }}
                  type="button"
                />
              </Col>
            </div>
          </Row>
          <Row style={{ position: 'relative' }}>
            <h3 className="sr-only" tabIndex={0} ref={tituloRef}>
              {descreverTitulo(showingFile)}
            </h3>
            <Loading loading={loading} contain={true} />
            {isImage() ? (
              <Col
                md={12}
                className="mt-xs ml-xs"
                style={{ display: 'flex', justifyContent: 'center' }}
              >
                <Image src={showingLink} alt="" responsive />
              </Col>
            ) : (
              <IFrameContainer>
                <IFrame
                  path={getViewerPath()}
                  title={
                    showingFile
                      ? nomeDocumento(showingFile)
                      : 'Nenhum documento aberto'
                  }
                />
              </IFrameContainer>
            )}
          </Row>
        </Col>
      </Row>
    </>
  );
};
